import type { JSX, ReactNode } from 'react';

import {
  Permission,
  planLinkReasonFor,
  subscriptionPlanPath,
  type EnforcedPlanFeature,
} from '@bookora/shared';
import { Alert } from '@bookora/ui';

import { LinkButton } from '@/components/link-button';
import { planLockReason } from '@/lib/reasons';
import { useCan } from '@/permissions/use-can';

import { PlanLockContext, isPlanLocked, usePlanLock } from './plan-access';
import {
  COMPARE_PLANS_LABEL,
  READ_ONLY_DESCRIPTION,
  UNKNOWN_PLAN_NAME,
  featureLockTitle,
} from './plan-lock-copy';

import './plan-lock.css';

export interface PlanLockBannerProps {
  feature: EnforcedPlanFeature;
  children?: ReactNode;
}

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  READ-ONLY AFTER A DOWNGRADE
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Owner D6: data a clinic created stays readable after a downgrade. Its
 * expenses, campaigns, payroll runs, share rules and lots are listed, opened
 * and exported as before; only NEW writes are closed until the plan is
 * upgraded. So the screen renders in full under one persistent notice (no
 * dismiss: hiding the sentence that explains why «ثبت» is grey helps nobody),
 * and its children render under `PlanLockContext` — every create button,
 * and every row action the server refuses on this plan (an edit, «ثبت
 * پرداخت», a delete, sending a campaign, settling a referral, a payslip's
 * status), reads `usePlanLockReason()` and passes it as `disabledReason`, so
 * it is focusable, announced and explains itself with «[why]. [what to do].».
 * What stays open stays open: reading, exporting, and stopping something
 * already running (pausing or cancelling a campaign).
 *
 * Anything a screen missed is still refused by the server with 402
 * FEATURE_NOT_IN_PLAN, and `PlanRefusalWatcher` says why.
 *
 * With the feature open the children render with no notice and a `null`
 * context value. The tree has the SAME shape either way — the provider, a
 * notice slot, the children — so plan access answering after the screen
 * mounted (the lock UI fails open while it loads) never remounts the screen
 * under the user: a half-typed search and an open drawer survive it.
 */
export function PlanLockBanner({ feature, children }: PlanLockBannerProps): JSX.Element {
  const lock = usePlanLock(feature);
  const canBilling = useCan(Permission.SETTINGS_BILLING);
  const locked = isPlanLocked(lock);
  const planName = lock.requiredPlanName ?? UNKNOWN_PLAN_NAME;
  return (
    <PlanLockContext.Provider value={locked ? planLockReason(planName, canBilling) : null}>
      {locked ? (
        <div className="bk-plan-lock-banner">
          <Alert
            type="info"
            showIcon
            title={featureLockTitle(feature, planName)}
            description={READ_ONLY_DESCRIPTION}
            action={
              canBilling ? (
                <LinkButton
                  size="small"
                  to={subscriptionPlanPath(lock.requiredPlanKey, planLinkReasonFor(feature))}
                >
                  {COMPARE_PLANS_LABEL}
                </LinkButton>
              ) : undefined
            }
          />
        </div>
      ) : null}
      {children}
    </PlanLockContext.Provider>
  );
}
